import { Injectable } from '@angular/core';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  linkWithCredential,
  EmailAuthProvider,
  ActionCodeSettings,
  Auth,
  User,
  UserCredential,
  AuthError
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  set,
  get,
  Database
} from 'firebase/database';
import { BehaviorSubject } from 'rxjs';

export interface UserProfile {
  uid: string;
  firstName?: string;
  lastName?: string;
  email?: string | null;
  country?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private auth: Auth;
  private db: Database;

  /** Firebase-recommended key for email link sign-in. */
  readonly emailForSignInStorageKey = 'emailForSignIn';
  private readonly emailLinkModeStorageKey = 'emailLinkMode';
  private readonly emailLinkReturnPathStorageKey = 'emailLinkReturnPath';

  currentUser$ = new BehaviorSubject<UserProfile | null>(null);
  isLoggedIn$ = new BehaviorSubject<boolean>(false);
  private lastLoginState = false;

  constructor() {
    this.auth = getAuth();
    this.db = getDatabase();
    this.bindAuthState();
  }

  private bindAuthState(): void {
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        await this.syncProfileFromAuthUser(user);
      } else {
        this.clearAuthState();
      }
    });
  }

  private async syncProfileFromAuthUser(user: User): Promise<void> {
    try {
      const snapshot = await get(ref(this.db, `users/${user.uid}`));

      if (snapshot.exists()) {
        const dbProfile = snapshot.val() as Partial<UserProfile>;
        const profile: UserProfile = {
          uid: user.uid,
          firstName: dbProfile.firstName,
          lastName: dbProfile.lastName,
          email: dbProfile.email ?? user.email,
          country: dbProfile.country
        };

        this.currentUser$.next(profile);
      } else {
        this.currentUser$.next({
          uid: user.uid,
          email: user.email
        });
      }

      this.isLoggedIn$.next(true);
      this.lastLoginState = true;
    } catch (error) {
      console.error('[AuthenticationService] Failed to sync auth profile', error);

      this.currentUser$.next({
        uid: user.uid,
        email: user.email
      });
      this.isLoggedIn$.next(true);
      this.lastLoginState = true;
    }
  }

  private clearAuthState(): void {
    this.currentUser$.next(null);
    this.isLoggedIn$.next(false);
    this.lastLoginState = false;
  }

  async signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    country: string
  ): Promise<UserCredential> {
    const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    const user = userCredential.user;

    const profile: UserProfile = {
      uid: user.uid,
      firstName,
      lastName,
      email: user.email,
      country
    };

    await set(ref(this.db, `users/${user.uid}`), profile);
    return userCredential;
  }

  async signIn(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  async signInWithGoogle(): Promise<UserCredential> {
    const provider = this.buildGoogleProvider();
    try {
      const credential = await signInWithPopup(this.auth, provider);
      await this.ensureUserProfile(credential.user);
      return credential;
    } catch (error) {
      const authError = error as AuthError;
      const popupRecoverableCodes = new Set([
        'auth/popup-blocked',
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request',
        'auth/operation-not-supported-in-this-environment'
      ]);

      if (popupRecoverableCodes.has(authError.code)) {
        await signInWithRedirect(this.auth, provider);
      }

      throw error;
    }
  }

  async handleGoogleRedirectResult(): Promise<UserCredential | null> {
    const credential = await getRedirectResult(this.auth);
    if (!credential) return null;

    await this.ensureUserProfile(credential.user);
    return credential;
  }

  private buildGoogleProvider(): GoogleAuthProvider {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    return provider;
  }

  private async ensureUserProfile(user: User): Promise<void> {
    const userRef = ref(this.db, `users/${user.uid}`);
    const snapshot = await get(userRef);
    if (snapshot.exists()) return;

    const profile: UserProfile = {
      uid: user.uid,
      firstName: user.displayName?.split(' ')[0] || '',
      lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
      email: user.email
    };
    await set(userRef, profile);
  }

  resetPassword(email: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, email);
  }

  /**
   * Starts passwordless email link flow. Persists `emailForSignIn` for same-device completion.
   * @param mode `signin` completes with signInWithEmailLink; `link` completes with linkWithCredential when a user is already signed in.
   */
  async sendEmailSignInLink(
    email: string,
    options: {
      mode: 'signin' | 'link';
      /** Path only, e.g. `/auth/log-in` — must be allowlisted in Firebase Auth authorized domains / action URL settings. */
      continuePath: string;
      /** In-app route after successful sign-in or link (path or absolute in-app URL). */
      postAuthNavigatePath?: string;
    }
  ): Promise<void> {
    const trimmed = email.trim();
    if (!trimmed) {
      throw new Error('Email is required');
    }
    if (typeof window === 'undefined') {
      throw new Error('Email link sign-in is only available in the browser');
    }
    const url = `${window.location.origin}${options.continuePath}`;
    const actionCodeSettings: ActionCodeSettings = {
      url,
      handleCodeInApp: true
    };
    window.localStorage.setItem(this.emailForSignInStorageKey, trimmed);
    window.localStorage.setItem(this.emailLinkModeStorageKey, options.mode);
    if (options.postAuthNavigatePath) {
      window.localStorage.setItem(this.emailLinkReturnPathStorageKey, options.postAuthNavigatePath);
    } else {
      window.localStorage.removeItem(this.emailLinkReturnPathStorageKey);
    }
    await sendSignInLinkToEmail(this.auth, trimmed, actionCodeSettings);
  }

  /**
   * If the current URL is an email sign-in link, completes sign-in or (when applicable) links the email provider.
   * Uses `emailForSignIn` in localStorage, or prompts when the link is opened on another device.
   */
  async tryCompleteEmailLinkSignInFromCurrentUrl(): Promise<
    | { status: 'none' }
    | { status: 'error'; message: string }
    | { status: 'signed_in'; postNavigatePath: string }
    | { status: 'linked'; postNavigatePath: string }
  > {
    if (typeof window === 'undefined') {
      return { status: 'none' };
    }
    const href = window.location.href;
    if (!isSignInWithEmailLink(this.auth, href)) {
      return { status: 'none' };
    }

    let email = window.localStorage.getItem(this.emailForSignInStorageKey);
    if (!email) {
      const prompted = window.prompt('Please provide your email for confirmation');
      email = prompted?.trim() || null;
    }
    if (!email) {
      return { status: 'error', message: 'Email is required to complete sign-in.' };
    }

    const mode = window.localStorage.getItem(this.emailLinkModeStorageKey);
    const postNavigatePath =
      window.localStorage.getItem(this.emailLinkReturnPathStorageKey) || '/ai/clarity';

    try {
      if (mode === 'link' && this.auth.currentUser) {
        const credential = EmailAuthProvider.credentialWithLink(email, href);
        await linkWithCredential(this.auth.currentUser, credential);
        this.clearEmailLinkLocalState();
        this.stripEmailLinkQueryFromUrl();
        return { status: 'linked', postNavigatePath };
      }

      await signInWithEmailLink(this.auth, email, href);
      const user = this.auth.currentUser;
      if (user) {
        await this.ensureUserProfile(user);
      }
      this.clearEmailLinkLocalState();
      this.stripEmailLinkQueryFromUrl();
      return { status: 'signed_in', postNavigatePath };
    } catch (error: any) {
      return { status: 'error', message: this.mapEmailLinkErrorMessage(error?.code) };
    }
  }

  /**
   * Link email-link credentials to the current user (e.g. reauthentication / add email sign-in to phone or social account).
   */
  async linkEmailCredentialFromUrl(email: string, url?: string): Promise<void> {
    if (!this.auth.currentUser) {
      throw new Error('You must be signed in to link an email sign-in method.');
    }
    const trimmed = email.trim();
    if (!trimmed) {
      throw new Error('Email is required');
    }
    const href =
      url ?? (typeof window !== 'undefined' ? window.location.href : '');
    const credential = EmailAuthProvider.credentialWithLink(trimmed, href);
    await linkWithCredential(this.auth.currentUser, credential);
  }

  private clearEmailLinkLocalState(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem(this.emailForSignInStorageKey);
    window.localStorage.removeItem(this.emailLinkModeStorageKey);
    window.localStorage.removeItem(this.emailLinkReturnPathStorageKey);
  }

  private stripEmailLinkQueryFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch {
      /* ignore */
    }
  }

  private mapEmailLinkErrorMessage(code: string | undefined): string {
    switch (code) {
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/invalid-action-code':
      case 'auth/expired-action-code':
        return 'This sign-in link is invalid or has expired. Request a new one.';
      case 'auth/credential-already-in-use':
        return 'That email is already linked to another account.';
      default:
        return 'Could not complete email link sign-in. Please try again.';
    }
  }

  get isLoggedIn(): boolean {
    return this.isLoggedIn$.getValue();
  }

  get currentLoginState(): boolean {
    return this.lastLoginState;
  }

  get currentUserSnapshot(): UserProfile | null {
    return this.currentUser$.getValue();
  }
}